<template>
  <div class="detalhes-page">
    <van-nav-bar :title="t('detalhes.title')" :border="false" class="custom-nav-bar">
      <template #left>
        <Button type="back" @click="onClickLeft" />
      </template>
    </van-nav-bar>
    <!-- 搜索框 -->
    <div class="search-bar">
      <input v-model="search" class="search-input" :placeholder="t('detalhes.searchPlaceholder')" />
      <span class="!flex-shrink-0 search-icon" @click="onSearch">
        <svg width="18" height="18" viewBox="0 0 1024 1024">
          <path fill="#8FC1A9"
            d="M1002.285 938.285l-241.143-241.143c61.714-81.143 98.286-181.143 98.286-289.143C859.428 184.571 674.857 0 447.999 0S36.571 184.571 36.571 408c0 223.429 184.571 408 411.428 408 108 0 208-36.571 289.143-98.286l241.143 241.143c14.286 14.286 37.143 14.286 51.429 0 14.286-14.286 14.286-37.143 0-51.429zM447.999 736c-181.143 0-328-146.857-328-328s146.857-328 328-328 328 146.857 328 328-146.857 328-328 328z" />
        </svg>
      </span>
    </div>

    <!-- 表格 -->
    <div class="table-container">
      <div class="table-header">
        <div class="table-cell">{{ t('detalhes.time') }}</div>
        <div class="table-cell">{{ t('detalhes.id') }}</div>
        <div class="table-cell">{{ t('detalhes.bet') }}</div>
        <div class="table-cell">{{ t('detalhes.contribution') }}</div>
      </div>

      <!-- 有数据时显示数据行 -->
      <div v-if="filteredData.length > 0" class="table-row" v-for="(row, idx) in filteredData" :key="idx">
        <div class="table-cell">{{ row.time || '--' }}</div>
        <div class="table-cell">{{ row.userId }}</div>
        <div class="table-cell">{{ row.betAmount || '0' }}</div>
        <div class="table-cell">{{ row.rebateReward || '0' }}</div>
      </div>

      <!-- 没有数据时显示缺省图 -->
      <div v-if="filteredData.length === 0" class="empty-data">
        <div class="flex justify-center items-center">
          <img :src="getAssetsFile('home/No_record@2x.png')" alt=""  class="max-h-[84px] max-w-[106.5px] w-auto h-auto">
        </div>
        <div class="empty-text">{{ t('detalhes.noRecords') }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from "@/components/Button"
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getUserContribution } from '@/api/game'
import { http } from '@/utils/request'
import { useUserStore } from '@/stores/modules/user'
import { getAssetsFile } from '@/utils/pub-use'
import dayjs from 'dayjs'
import { formatDateTime, getStartOfToday, getStartOfDaysAgo } from '@/utils/date'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const onClickLeft = () => router.back()

const search = ref('')
const tableData = ref([])
const loading = ref(false)
const promoterConfig = ref(null)

// 分页参数
const pageNo = ref(1)
const pageSize = ref(10)
const hasMore = ref(true)

// 格式化时间
const formatTime = (time: string | number) => {
  if (!time) return ''
  return formatDateTime(time)
}

// 用户名脱敏函数
const maskUserName = (userName: string) => {
  if (!userName || userName.length <= 2) return userName

  const firstChar = userName.charAt(0)
  const lastChar = userName.charAt(userName.length - 1)

  return `${firstChar}***${lastChar}`
}

// 根据 settle 获取时间范围
const getTimeRange = (settle: number) => {
  switch (settle) {
    case 1: // 日 - 今天到今天结束
      return {
        startTime: getStartOfToday(),
        endTime: getStartOfToday()
      }
    case 2: // 周 - 7天前到今天
      return {
        startTime: getStartOfDaysAgo(7),
        endTime: getStartOfToday()
      }
    case 3: // 月 - 30天前到今天
      return {
        startTime: getStartOfDaysAgo(30),
        endTime: getStartOfToday()
      }
    default:
      return {
        startTime: getStartOfToday(),
        endTime: getStartOfToday()
      }
  }
}

// 获取推广配置
const fetchPromoterConfig = async () => {
  try {
    const res = await http.get('/center/user-promoter/getPromoterConfig')
    if (res && res.data) {
      promoterConfig.value = res.data
    }
  } catch (error) {
    console.error('获取推广配置失败:', error)
  }
}

// 获取表格数据
const fetchTableData = async (isAppend = false) => {
  if (loading.value) return

  loading.value = true
  try {
    // 获取时间范围
    const timeRange = getTimeRange(promoterConfig.value?.settle)

    // 使用当前用户的ID，而不是写死的4672
    const currentUserId = userStore.userInfo.id

    // 构建基础参数 - 使用当前用户ID
    const baseParams = `pageNo=${pageNo.value}&pageSize=${pageSize.value}&promoterId=${currentUserId}&offset=0`

    // 使用和 casseindex 页面一样的 URL 格式
    let url = `/center/user-promoter/getUserContribution?${baseParams}&tair=  ${1}    `

    // 如果有搜索ID，添加到URL中
    if (search.value) {
      url += `&userId=${search.value}`
    }

    console.log('请求URL:', url)

    // 直接使用 http.get 发送请求
    const response = await http.get(url)
    if (response && response.data && response.data.list) {
      const newData = response.data.list

      if (isAppend) {
        // 追加数据
        tableData.value = [...tableData.value, ...newData]
      } else {
        // 替换数据
        tableData.value = newData
      }

      // 判断是否还有更多数据
      hasMore.value = newData.length === pageSize.value
    }
  } catch (error) {
    console.error('获取数据失败:', error)
    if (!isAppend) {
      tableData.value = []
    }
  } finally {
    loading.value = false
  }
}

// 搜索功能
const onSearch = () => {
  pageNo.value = 1
  hasMore.value = true
  tableData.value = []
  fetchTableData()
}

// 过滤数据
const filteredData = computed(() => {
  if (!search.value) return tableData.value

  return tableData.value.filter(row =>
    row.userName && row.userName.includes(search.value)
  )
})

onMounted(async () => {
  // 先获取推广配置
  await fetchPromoterConfig()
  // 然后获取表格数据
  await fetchTableData()
})
</script>

<style lang="scss" scoped>
.custom-nav-bar {
  box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.3), 0px -2px 22px 0px rgba(53, 189, 128, 0.61);
  height: 56px;

  &>* {
    height: 100%;
  }
}

.detalhes-page {
  background: #004D37;
  min-height: 100vh;
  padding: 0;
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 0 8px 0;
  width: 100%;
  font-size: 11px;
  position: relative;
}

.search-input {
  width: 95%;
  height: 42px;
  border-radius: 8px;
  border: 1px solid #009F76;
  outline: none;
  padding: 0 40px 0 12px;
  background: #004D37;
  color: #50A363;
  font-size: 15px;
  box-shadow: none;
}

.search-input::placeholder {
  color: #2C8564;
  font-size: 13px;
}

.search-icon {
  position: absolute;
  right: 10%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.table-container {
  margin: 12px 8px 0 8px;
  background: transparent;
  border-radius: 4px;
  overflow: hidden;
}

.table-header {
  display: flex;
  background: #00563A;
  color: #669A7F;
  font-weight: bold;
  font-size: 11px;
  padding: 10px 0;
}

.table-row {
  display: flex;
  color: #50A363;
  font-size: 10px;
  padding: 8px 0;
}

.table-cell {
  flex: 1;
  text-align: center;
  min-width: 0;
  word-break: break-all;
}

.table-header .table-cell {
  font-weight: bold;
}

/* 缺省图样式 */
.empty-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 115px 0;
}

.empty-text {
  color: #ADB1B0;
  font-size: 11px;
  margin-top: 21px;
}
</style>

<route lang="json5">
{
  name: 'Detalhes',
  meta: {
    title: 'Detalhes',
    i18n: 'menus.Detalhes'
  }
}
</route>
